<template>
  <div class="flex flex-col bg-white p-6 md:flex-row md:space-x-12">
    <div class="relative mb-8 md:w-2/5 lg:mb-0 lg:max-w-[425px]">
      <ImageGallery :images="product.images">
        <template #badges>
          <InnoDiscountBadge :properties="product.properties!" />
        </template>
      </ImageGallery>

      <div
        class="absolute -right-4 -top-4 z-[2] flex flex-col gap-2 rounded-3xl bg-white px-2 py-3.5 empty:hidden lg:-right-3 lg:px-1.5 lg:py-2"
      >
        <AddToList :product="product" custom-class="w-6 h-6 lg:w-8 lg:h-8" />
      </div>

      <div class="mb-4 mt-2">
        <InnoDiscountContent :properties="product.properties!" />
      </div>

      <div class="mt-2">
        <InnoAddToCompare v-if="$cfg.product_compare_enabled" :product="product" />
      </div>
      <template v-if="psInFoodProduct && psInFoodProduct.products?.length">
        <VcButton :disabled="loadingPsInFood" size="md" class="mt-6 w-full uppercase" @click="openPsInFoodModal()">
          {{ $t("custom.ps_in_food.button_modal") }}
        </VcButton>
      </template>
    </div>

    <div class="flex max-w-[650px] flex-col md:w-3/5">
      <div>
        <VcTypography tag="h2" class="text-5xl">{{ product.name }}</VcTypography>
        <p class="mt-2 text-xl">{{ shortDescription }}</p>
        <div v-if="isAuthenticated" :product="product" class="mt-6 flex w-56 flex-col">
          <VcItemPrice
            v-if="product.sapStockStatus != 3 && product.availabilityData.isBuyable"
            :properties="product.properties"
            :value="product.price"
            class="text-3xl"
          />

          <div class="mt-2 flex">
            <InnoInStock
              :is-in-stock="product.availabilityData?.isInStock"
              :stock-type="product.sapStockStatus"
              :is-digital="isDigital"
              :quantity="product.availabilityData?.availableQuantity"
            />
          </div>

          <div class="mt-7 md:mt-5">
            <InnoAddToCart :product="product" />
          </div>

          <div class="w-fit">
            <CountInCart class="mt-6 px-2" :product-id="product.id" />
          </div>
        </div>

        <div v-else>
          <VcButton :to="{ name: 'SignIn' }" size="md" class="mt-6 w-60">
            {{ $t("custom.product_details.login") }}
          </VcButton>
        </div>
      </div>
    </div>
  </div>

  <div class="!mt-0 flex flex-col bg-white p-6 lg:flex-row lg:space-x-12">
    <div class="mb-8 md:w-2/5 lg:mb-0 lg:max-w-[425px]">
      <component
        :is="specificationSection?.type"
        :key="specificationSection?.id || 'productspecsA32'"
        :model="specificationSection"
        :product="product"
      />
    </div>

    <div class="flex max-w-[650px] flex-col md:w-3/5">
      <VcTypography tag="h2" class="mb-4 text-4xl">{{
        $t("shared.catalog.product_details.description_block_title")
      }}</VcTypography>

      <VcCollapsibleContent max-height="18.75rem" class="text-base text-neutral-600">
        <VcMarkdownRender :src="description" />
      </VcCollapsibleContent>

      <div class="mt-6">
        <VcTypography tag="h2">{{ $t("custom.cta.title") }}</VcTypography>
        <p v-html="$t('custom.cta.text')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import _productTemplate from "@/config/product.json";
import { ProductType } from "@/core/enums";
import { getDynamicPropertyValue } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { InnoAddToCart } from "@/shared/cart";
import { ImageGallery, InnoDiscountContent, InnoDiscountBadge, CountInCart, InnoInStock } from "@/shared/catalog";
import { InnoAddToCompare } from "@/shared/compare";
import { useModal } from "@/shared/modal";
import { AddToList } from "@/shared/wishlists";
import { InnoPsInFoodModal } from "@/ui-kit/components";
import type { Product, ProductSearchResultType } from "@/core/api/graphql/types";
import type { IPageTemplate } from "@/shared/static-content";
import type { PropType } from "vue";

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },

  psInFoodProduct: {
    type: Object as PropType<ProductSearchResultType>,
    required: false,
  },

  loadingPsInFood: {
    type: Boolean,
    required: false,
  },
});

const { isAuthenticated } = useUser();
const productTemplate = _productTemplate as IPageTemplate;

const isDigital = computed<boolean>(() => props.product.productType === ProductType.Digital);

const SHORT_DESCRIPTION_PROPERTY = "korteomschrijving_property"; // was KorteOmschrijving but was changed as result of solution for duplicate properties
const DESCRIPTION_PROPERTY = "omschrijving_property";
const productInfo = productTemplate?.content?.find((item) => item?.type === "product-info");

const specificationSection = productInfo?.blocks?.find((item) => item?.type === "product-properties");

const shortDescription = computed<string>(
  () => getDynamicPropertyValue(props.product?.properties, SHORT_DESCRIPTION_PROPERTY) ?? "",
);

const description = computed<string>(
  () => getDynamicPropertyValue(props.product?.properties, DESCRIPTION_PROPERTY) ?? "",
);

const { openModal } = useModal();

function openPsInFoodModal() {
  openModal({
    component: InnoPsInFoodModal,
    props: {
      psProduct: props.psInFoodProduct,
    },
  });
}
</script>

<style>
@media (min-width: 1200px) {
  .custom-titlte-prod-detail {
    font-size: 3rem;
    line-height: 52px;
  }
}
</style>
